import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

const AuthContext = createContext(null);

// The session cookie is httpOnly, so this readable marker tells us whether a
// session lookup is worth making. It holds no credentials.
const MARKER = "seleksa_signed_in";
// The hidden developer login is deliberately not listed here — that route must
// only ever appear in App.js (guarded by tests/test_iteration5.py).
const PUBLIC_PATHS = ["/", "/login", "/signup", "/client-login"];

const isPublicPath = (path) => PUBLIC_PATHS.includes(path) || path.startsWith("/g/");

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      localStorage.setItem(MARKER, "1");
    } catch {
      setUser(null);
      localStorage.removeItem(MARKER);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback -> let AuthCallback exchange session_id first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // On public pages, only look up a session if the visitor has signed in before.
    if (isPublicPath(window.location.pathname) && !localStorage.getItem(MARKER)) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    await checkAuth();
    return res.data;
  }, [checkAuth]);

  const signupStudio = useCallback(async (payload) => {
    const res = await api.post("/auth/studio-signup", payload);
    await checkAuth();
    return res.data;
  }, [checkAuth]);

  const clientLogin = useCallback(async (email, access_code) => {
    const res = await api.post("/auth/client-login", { email, access_code });
    await checkAuth();
    return res.data;
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      logError("Logout request failed", err);
    }
    setUser(null);
    localStorage.removeItem(MARKER);
  }, []);

  const isOwner = user?.tenant_role === "owner";
  const isStaff = user?.tenant_role === "owner" || user?.tenant_role === "admin";
  const isClient = user?.tenant_role === "client";
  const isPlatformOwner = user?.platform_role === "platform_owner";

  const value = useMemo(
    () => ({ user, setUser, loading, login, signupStudio, clientLogin, logout, checkAuth,
             isOwner, isStaff, isClient, isPlatformOwner }),
    [user, loading, checkAuth, login, signupStudio, clientLogin, logout,
     isOwner, isStaff, isClient, isPlatformOwner]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
